import { useApolloClient, useLazyQuery } from '@apollo/client';
import { packageToFormState } from '@openx/components-package/components/PackageForm/utils/formStateConverter';
import { usePackageContext } from '@openx/components-package/utils/context/PackageContext';
import { determineFormType, getNewPath } from '@openx/components-package/utils/formNavigation';
import { useStandaloneContextualSegmentsEnabled } from '@openx/components-targeting/Targeting/ContextualSegments/featureFlags';
import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal';
import type { PackageWithTargeting } from '@openx/types/package';
import type { ClonePackageFormParam, UseCloneForm } from '@openx/types/package/PackageHookTypes';
import { produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { GetPackageDocument } from '../../../graphql/query/packages/GetPackage.generated';
import { trackContextualSegmentsSaved } from '../../targeting/utils/trackContextualSegmentsSaved';

export const useCloneForm: UseCloneForm = () => {
  const { usePackageClone } = usePackageContext();
  const [getPackage] = useLazyQuery(GetPackageDocument);
  const { clonePackage: clonePackageAction, isCloning } = usePackageClone();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const isStandaloneContextualSegments = useStandaloneContextualSegmentsEnabled();

  const historyState = location.state;

  const clonePackage = useCallback(
    async ({ diff, pckg, setSubmitErrors, values, initialValues }: ClonePackageFormParam) => {
      const { errors, fulfilled } = await clonePackageAction({ editModePayload: diff, packages: [pckg] });

      if (errors.length) {
        const errorMessages: string[] = errors[0].graphQLErrors?.flatMap(error => {
          const firstErrorMessage = error.extensions.errors?.[0]?.message;
          return firstErrorMessage ? [firstErrorMessage] : [];
        });

        const fallbackError = [t('Failed to clone package.')];
        setSubmitErrors(errorMessages.length ? errorMessages : fallbackError);
        return;
      }

      if (fulfilled.length) {
        const clonedPackage = fulfilled[0].clone;

        const { data } = await getPackage({
          variables: { id: clonedPackage.uid },
        });

        const nextPackage = data?.packageById?.obj as PackageWithTargeting;

        setSubmitErrors([]);
        apolloClient.cache.evict({ fieldName: 'packages', id: 'ROOT_QUERY' });
        apolloClient.cache.evict({ fieldName: 'packageById', id: 'ROOT_QUERY' });
        apolloClient.cache.evict({ fieldName: 'targetablePackages', id: 'ROOT_QUERY' });
        apolloClient.cache.evict({ fieldName: 'openxCuratedPackages', id: 'ROOT_QUERY' });

        const type = determineFormType(historyState);

        /**
         * Edits saved as a new package, so it is tracked as a package creation.
         * `had_segments_before` reflects the source package's segments, so unlike a plain create it can be true.
         */
        trackContextualSegmentsSaved({
          entity: 'package',
          isEdit: false,
          isStandaloneContextualSegments,
          targeting: values.targeting,
          initialTargeting: initialValues.targeting,
          fromDealForm: type === 'deal',
        });

        if (historyState?.redirectPath && type === 'deal') {
          const nextDeal = produce<DealFormDataWithTargeting>(historyState.deal, draft => {
            draft.targeting = cloneDeep(packageToFormState(nextPackage));
            draft.package_uid = nextPackage.uid;
            draft.package_id = nextPackage.id;
          });

          navigate(historyState?.redirectPath, { replace: true, state: { deal: nextDeal } });
          return;
        } else if (type) {
          const newPath = getNewPath({ [type]: { ...historyState[type] } }, type);

          navigate(newPath, {
            replace: true,
            state: { ...historyState, deal: { ...historyState.deal, [dealFormField.PACKAGE_UID]: clonedPackage.uid } },
          });
        } else {
          navigate(`/inventory/packages/${clonedPackage.uid}/edit`);
        }
      }
    },
    [clonePackageAction, navigate, historyState, t, apolloClient, getPackage, isStandaloneContextualSegments]
  );

  return { clonePackage, isCloning };
};
